import type { AssignableRolesDto } from '@lib/dto/user.dto'
import { ORGANIZATION_ADMIN_NAME } from '@lib/constants/permissions'
import { Select } from '@/components/ui/select'
import { companyRoleOf, reachOf, staffRoleKey, toPersonRoleKey, type PersonRoleKey } from '@/components/people/personRole'

interface RolePickerProps {
  id: string
  value: PersonRoleKey | null
  onChange: (next: PersonRoleKey) => void
  /** The company's roles, and which of them the viewer may give. */
  roles: AssignableRolesDto
  /**
   * Only roles that work at assigned venues: inviting from a venue's staff, the
   * person works at that venue, so a company-wide role (or organization admin,
   * or a client) isn't what is being asked for.
   */
  venuesOnly?: boolean
  /** The role they hold now, listed even when the viewer can't give it, so the select can show it. */
  current?: PersonRoleKey | null
  disabled?: boolean
}

/**
 * Picks someone's role: the company's roles the viewer may give
 * (docs/custom-roles.spec.md, "People and the guardrail"), and for organization
 * admins, organization admin and client too.
 */
export function RolePicker({ id, value, onChange, roles, venuesOnly = false, current = null, disabled = false }: RolePickerProps) {
  const companyRoles = roles.roles.filter(
    (role) => (role.mayGive || staffRoleKey(role.id) === current) && (!venuesOnly || role.reach === 'venues'),
  )
  return (
    <Select
      id={id}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => {
        const next = toPersonRoleKey(e.target.value)
        if (next !== null) onChange(next)
      }}
    >
      {value === null ? <option value="">Choose a role…</option> : null}
      {roles.organizationAdmin && !venuesOnly ? <option value="organizationAdmin">{ORGANIZATION_ADMIN_NAME}</option> : null}
      {companyRoles.map((role) => (
        <option key={role.id} value={staffRoleKey(role.id)} disabled={!role.mayGive}>
          {role.name}
        </option>
      ))}
      {roles.client && !venuesOnly ? <option value="client">Client</option> : null}
    </Select>
  )
}

/** What the chosen role means for where someone works, for the line under the picker. */
export function roleReachHint(key: PersonRoleKey | null, roles: AssignableRolesDto): string | undefined {
  if (key === 'organizationAdmin') return 'Can do everything, across the company.'
  if (key === 'client') return 'A couple, who sees their own events.'
  const reach = reachOf(key, roles)
  if (reach === 'company') return `${companyRoleOf(key, roles)?.name ?? 'This role'} works across the whole company.`
  if (reach === 'venues') return 'Works at the venues they’re given.'
  return undefined
}
